import { useEffect, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Package,
  ChevronDown,
  ChevronUp,
  Zap,
  Server,
  Database,
  Cloud,
  Shield,
  Cpu,
  Star,
  Gem,
  Crown,
  Activity,
  Filter,
  Link2,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/adminApi';

const CATEGORY_OPTIONS = [
  'compute',
  'storage',
  'database',
  'cache',
  'queue',
  'loadbalancer',
  'networking',
  'security',
  'monitoring',
];

const RARITY_OPTIONS = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const TIER_OPTIONS = ['basic', 'standard', 'pro', 'enterprise'];
const PROVIDER_OPTIONS = ['aws', 'azure', 'gcp', 'open_source', 'generic'];

const RARITY_CONFIG = {
  common: { name: 'Common', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: null },
  uncommon: { name: 'Uncommon', color: 'bg-green-100 text-green-700 border-green-300', icon: null },
  rare: { name: 'Rare', color: 'bg-blue-100 text-blue-700 border-blue-400', icon: Star },
  epic: { name: 'Epic', color: 'bg-purple-100 text-purple-700 border-purple-400', icon: Gem },
  legendary: { name: 'Legendary', color: 'bg-amber-100 text-amber-700 border-amber-400', icon: Crown },
};

const TIER_CONFIG = {
  basic: { name: 'Basic', color: 'bg-gray-50 text-gray-600' },
  standard: { name: 'Standard', color: 'bg-blue-50 text-blue-600' },
  pro: { name: 'Pro', color: 'bg-purple-50 text-purple-600' },
  enterprise: { name: 'Enterprise', color: 'bg-amber-50 text-amber-600' },
};

const PROVIDER_CONFIG = {
  aws: { name: 'AWS', color: 'bg-orange-100 text-orange-700' },
  azure: { name: 'Azure', color: 'bg-blue-100 text-blue-700' },
  gcp: { name: 'GCP', color: 'bg-red-100 text-red-700' },
  open_source: { name: 'Open Source', color: 'bg-green-100 text-green-700' },
  generic: { name: 'Generic', color: 'bg-gray-100 text-gray-700' },
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'compute': return Cpu;
    case 'storage': return Database;
    case 'database': return Database;
    case 'cache': return Zap;
    case 'networking': return Cloud;
    case 'security': return Shield;
    case 'loadbalancer': return Server;
    case 'queue': return Activity;
    case 'monitoring': return Activity;
    default: return Package;
  }
};

const RarityBadge = ({ rarity }) => {
  const config = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.name}
    </span>
  );
};

const TierBadge = ({ tier }) => {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.basic;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.name}
    </span>
  );
};

const ProviderBadge = ({ provider }) => {
  const config = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.generic;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.name}
    </span>
  );
};

const DEFAULT_STATS = {
  performance: {
    qps_read: 10000,
    qps_write: 5000,
    latency_p50_ms: 5,
    latency_p99_ms: 50,
    throughput_mbps: 100,
  },
  reliability: {
    sla_percentage: 99.9,
    availability_zones: 3,
    auto_failover: false,
    durability_nines: 9,
  },
  scalability: {
    horizontal_scaling: true,
    auto_scaling: false,
    max_nodes: 100,
    max_instances: 1000,
  },
  security: {
    encryption_at_rest: true,
    encryption_in_transit: true,
    compliance: [],
  },
};

const SYNERGY_TYPE_CONFIG = {
  positive: { name: 'Positive', color: 'bg-green-100 text-green-700 border-green-300', icon: Sparkles },
  negative: { name: 'Negative', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle },
  neutral: { name: 'Neutral', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Link2 },
};

const ComponentEditor = () => {
  const { components, loading, fetchComponents, createComponent, updateComponent, deleteComponent } = useAdminStore();
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRarity, setFilterRarity] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  // Synergy state
  const [componentSynergies, setComponentSynergies] = useState([]);
  const [loadingSynergies, setLoadingSynergies] = useState(false);
  const [showSynergyForm, setShowSynergyForm] = useState(false);
  const [synergyForm, setSynergyForm] = useState({
    component_b_id: '',
    synergy_type: 'positive',
    synergy_name: '',
    description: '',
    bonus_points: 10,
    penalty_points: 0,
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'compute',
    rarity: 'common',
    tier: 'basic',
    provider: 'generic',
    unlock_cost: 0,
    is_starter: false,
    cost_profile: {
      monthly_cost: 100,
      provider: 'generic',
    },
    stats: DEFAULT_STATS,
  });

  useEffect(() => {
    fetchComponents();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'compute',
      rarity: 'common',
      tier: 'basic',
      provider: 'generic',
      unlock_cost: 0,
      is_starter: false,
      cost_profile: {
        monthly_cost: 100,
        provider: 'generic',
      },
      stats: DEFAULT_STATS,
    });
    setSelectedComponent(null);
    setIsEditing(false);
    setIsCreating(false);
    setActiveTab('basic');
  };

  const fetchComponentSynergies = async (componentId) => {
    setLoadingSynergies(true);
    try {
      const response = await adminAPI.getComponentSynergies(componentId);
      setComponentSynergies(response.data.synergies || []);
    } catch (err) {
      console.error('Failed to fetch synergies:', err);
      setComponentSynergies([]);
    } finally {
      setLoadingSynergies(false);
    }
  };

  const handleEdit = (component) => {
    setSelectedComponent(component);
    setFormData({
      name: component.name || '',
      description: component.description || '',
      category: component.category || 'compute',
      rarity: component.rarity || 'common',
      tier: component.tier || 'basic',
      provider: component.provider || component.cost_profile?.provider || 'generic',
      unlock_cost: component.unlock_cost || 0,
      is_starter: component.is_starter || false,
      cost_profile: component.cost_profile || { monthly_cost: 100, provider: 'generic' },
      stats: component.stats || DEFAULT_STATS,
    });
    setIsEditing(true);
    setIsCreating(false);
    setActiveTab('basic');
    // Fetch synergies for this component
    fetchComponentSynergies(component.id);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        ...formData,
        cost_profile: {
          ...formData.cost_profile,
          provider: formData.provider,
        },
      };

      if (isCreating) {
        await createComponent(payload);
        toast.success('Component created successfully');
      } else if (isEditing && selectedComponent) {
        await updateComponent(selectedComponent.id, payload);
        toast.success('Component updated successfully');
      }
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save component');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (component) => {
    if (!confirm(`Delete component "${component.name}"? This cannot be undone.`)) return;

    try {
      await deleteComponent(component.id);
      toast.success('Component deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete component');
    }
  };

  const resetSynergyForm = () => {
    setSynergyForm({
      component_b_id: '',
      synergy_type: 'positive',
      synergy_name: '',
      description: '',
      bonus_points: 10,
      penalty_points: 0,
    });
    setShowSynergyForm(false);
  };

  const handleCreateSynergy = async () => {
    if (!synergyForm.component_b_id || !synergyForm.synergy_name) {
      toast.error('Please select a component and provide a synergy name');
      return;
    }

    setProcessing(true);
    try {
      await adminAPI.createSynergy({
        component_a_id: selectedComponent.id,
        component_b_id: parseInt(synergyForm.component_b_id),
        synergy_type: synergyForm.synergy_type,
        synergy_name: synergyForm.synergy_name,
        description: synergyForm.description,
        bonus_points: synergyForm.synergy_type === 'positive' ? synergyForm.bonus_points : 0,
        penalty_points: synergyForm.synergy_type === 'negative' ? synergyForm.penalty_points : 0,
      });
      toast.success('Synergy created successfully');
      resetSynergyForm();
      fetchComponentSynergies(selectedComponent.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create synergy');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteSynergy = async (synergyId) => {
    if (!confirm('Delete this synergy?')) return;

    try {
      await adminAPI.deleteSynergy(synergyId);
      toast.success('Synergy deleted');
      fetchComponentSynergies(selectedComponent.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete synergy');
    }
  };

  const updateStats = (category, field, value) => {
    setFormData({
      ...formData,
      stats: {
        ...formData.stats,
        [category]: {
          ...formData.stats[category],
          [field]: value,
        },
      },
    });
  };

  const filteredComponents = components.filter((c) => {
    if (filterCategory && c.category !== filterCategory) return false;
    if (filterRarity && (c.rarity || 'common') !== filterRarity) return false;
    if (filterProvider && (c.provider || c.cost_profile?.provider || 'generic') !== filterProvider) return false;
    return true;
  });

  // Stats summary for list display
  const getStatsSummary = (stats) => {
    if (!stats || !stats.performance) return null;
    const perf = stats.performance;
    return `${perf.qps_read?.toLocaleString() || '?'} QPS, ${perf.latency_p99_ms || '?'}ms P99`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Component Editor</h2>
          <p className="text-gray-500">Manage system design components with rarity, tiers, and stats</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          New Component
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Rarity</label>
            <select
              value={filterRarity}
              onChange={(e) => setFilterRarity(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {RARITY_OPTIONS.map((r) => (
                <option key={r} value={r}>{RARITY_CONFIG[r].name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Provider</label>
            <select
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {PROVIDER_OPTIONS.map((p) => (
                <option key={p} value={p}>{PROVIDER_CONFIG[p].name}</option>
              ))}
            </select>
          </div>
          <div className="ml-auto text-sm text-gray-500">
            Showing {filteredComponents.length} of {components.length}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Component List */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">Components ({filteredComponents.length})</h3>
          </div>
          <div className="divide-y max-h-[700px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : filteredComponents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No components found
              </div>
            ) : (
              filteredComponents.map((component) => {
                const IconComponent = getCategoryIcon(component.category);
                const rarity = component.rarity || 'common';
                const tier = component.tier || 'basic';
                const provider = component.provider || component.cost_profile?.provider || 'generic';
                const rarityConfig = RARITY_CONFIG[rarity];

                return (
                  <div
                    key={component.id}
                    className={`${selectedComponent?.id === component.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className="px-4 py-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <IconComponent className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <span className="font-medium text-gray-900 truncate">{component.name}</span>
                            {component.is_starter && (
                              <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded flex-shrink-0">
                                Starter
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <RarityBadge rarity={rarity} />
                            <TierBadge tier={tier} />
                            <ProviderBadge provider={provider} />
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span className="capitalize">{component.category}</span>
                            <span>${component.cost_profile?.monthly_cost || 0}/mo</span>
                            {component.unlock_cost > 0 && (
                              <span className="text-orange-600">{component.unlock_cost} DP</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => setExpandedId(expandedId === component.id ? null : component.id)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          >
                            {expandedId === component.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(component)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(component)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {expandedId === component.id && (
                        <div className="mt-3 pt-3 border-t text-sm">
                          <p className="text-gray-600 mb-2">{component.description}</p>
                          {component.stats && (
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Stats:</span> {getStatsSummary(component.stats) || 'Not configured'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Editor Form */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">
              {isCreating ? 'New Component' : isEditing ? 'Edit Component' : 'Component Editor'}
            </h3>
            {(isEditing || isCreating) && (
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {!isEditing && !isCreating ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Select a component to edit or create a new one</p>
            </div>
          ) : (
            <div className="flex flex-col h-[700px]">
              {/* Tabs */}
              <div className="flex border-b px-4 overflow-x-auto">
                {[
                  { id: 'basic', label: 'Basic Info' },
                  { id: 'enrichment', label: 'Rarity & Tier' },
                  { id: 'stats', label: 'Stats' },
                  { id: 'cost', label: 'Cost Profile' },
                  ...(isEditing ? [{ id: 'synergies', label: 'Synergies' }] : []),
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {activeTab === 'basic' && (
                  <>
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., AWS ElastiCache Redis"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe the component's purpose and features..."
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      >
                        {CATEGORY_OPTIONS.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Is Starter */}
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_starter}
                          onChange={(e) => setFormData({ ...formData, is_starter: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Starter Component (available to all users)</span>
                      </label>
                    </div>
                  </>
                )}

                {activeTab === 'enrichment' && (
                  <>
                    {/* Provider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                      <select
                        value={formData.provider}
                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      >
                        {PROVIDER_OPTIONS.map((p) => (
                          <option key={p} value={p}>{PROVIDER_CONFIG[p].name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Rarity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rarity</label>
                      <div className="flex flex-wrap gap-2">
                        {RARITY_OPTIONS.map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setFormData({ ...formData, rarity: r })}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              formData.rarity === r
                                ? RARITY_CONFIG[r].color
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            {RARITY_CONFIG[r].name}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Rarity affects visual styling and component unlock cost multiplier
                      </p>
                    </div>

                    {/* Tier */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                      <div className="flex flex-wrap gap-2">
                        {TIER_OPTIONS.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setFormData({ ...formData, tier: t })}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              formData.tier === t
                                ? TIER_CONFIG[t].color + ' border-current'
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            {TIER_CONFIG[t].name}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Tier represents the service level and pricing tier
                      </p>
                    </div>

                    {/* Unlock Cost */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unlock Cost (DP)</label>
                      <input
                        type="number"
                        value={formData.unlock_cost}
                        onChange={(e) => setFormData({ ...formData, unlock_cost: parseInt(e.target.value) || 0 })}
                        min="0"
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Design Points required to unlock. Set to 0 for starter components.
                      </p>
                    </div>
                  </>
                )}

                {activeTab === 'stats' && (
                  <>
                    {/* Performance Stats */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-700 mb-3">Performance</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Read QPS</label>
                          <input
                            type="number"
                            value={formData.stats?.performance?.qps_read || 0}
                            onChange={(e) => updateStats('performance', 'qps_read', parseInt(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Write QPS</label>
                          <input
                            type="number"
                            value={formData.stats?.performance?.qps_write || 0}
                            onChange={(e) => updateStats('performance', 'qps_write', parseInt(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">P50 Latency (ms)</label>
                          <input
                            type="number"
                            value={formData.stats?.performance?.latency_p50_ms || 0}
                            onChange={(e) => updateStats('performance', 'latency_p50_ms', parseFloat(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">P99 Latency (ms)</label>
                          <input
                            type="number"
                            value={formData.stats?.performance?.latency_p99_ms || 0}
                            onChange={(e) => updateStats('performance', 'latency_p99_ms', parseFloat(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Throughput (MB/s)</label>
                          <input
                            type="number"
                            value={formData.stats?.performance?.throughput_mbps || 0}
                            onChange={(e) => updateStats('performance', 'throughput_mbps', parseInt(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Reliability Stats */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-700 mb-3">Reliability</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">SLA (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.stats?.reliability?.sla_percentage || 99.9}
                            onChange={(e) => updateStats('reliability', 'sla_percentage', parseFloat(e.target.value) || 99.9)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Availability Zones</label>
                          <input
                            type="number"
                            value={formData.stats?.reliability?.availability_zones || 1}
                            onChange={(e) => updateStats('reliability', 'availability_zones', parseInt(e.target.value) || 1)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Durability (9s)</label>
                          <input
                            type="number"
                            value={formData.stats?.reliability?.durability_nines || 9}
                            onChange={(e) => updateStats('reliability', 'durability_nines', parseInt(e.target.value) || 9)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.stats?.reliability?.auto_failover || false}
                              onChange={(e) => updateStats('reliability', 'auto_failover', e.target.checked)}
                              className="w-4 h-4 text-green-600 rounded"
                            />
                            <span className="text-xs text-gray-600">Auto Failover</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Scalability Stats */}
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-purple-700 mb-3">Scalability</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Max Nodes</label>
                          <input
                            type="number"
                            value={formData.stats?.scalability?.max_nodes || 0}
                            onChange={(e) => updateStats('scalability', 'max_nodes', parseInt(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Max Instances</label>
                          <input
                            type="number"
                            value={formData.stats?.scalability?.max_instances || 0}
                            onChange={(e) => updateStats('scalability', 'max_instances', parseInt(e.target.value) || 0)}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.stats?.scalability?.horizontal_scaling || false}
                              onChange={(e) => updateStats('scalability', 'horizontal_scaling', e.target.checked)}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <span className="text-xs text-gray-600">Horizontal Scaling</span>
                          </label>
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.stats?.scalability?.auto_scaling || false}
                              onChange={(e) => updateStats('scalability', 'auto_scaling', e.target.checked)}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <span className="text-xs text-gray-600">Auto Scaling</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Security Stats */}
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-700 mb-3">Security</h4>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.stats?.security?.encryption_at_rest || false}
                            onChange={(e) => updateStats('security', 'encryption_at_rest', e.target.checked)}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                          <span className="text-xs text-gray-600">Encryption at Rest</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.stats?.security?.encryption_in_transit || false}
                            onChange={(e) => updateStats('security', 'encryption_in_transit', e.target.checked)}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                          <span className="text-xs text-gray-600">Encryption in Transit</span>
                        </label>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Compliance (comma-separated)</label>
                          <input
                            type="text"
                            value={(formData.stats?.security?.compliance || []).join(', ')}
                            onChange={(e) => updateStats('security', 'compliance', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="SOC2, HIPAA, PCI-DSS"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'cost' && (
                  <>
                    {/* Monthly Cost */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Cost ($)</label>
                      <input
                        type="number"
                        value={formData.cost_profile?.monthly_cost || 0}
                        onChange={(e) => setFormData({
                          ...formData,
                          cost_profile: { ...formData.cost_profile, monthly_cost: parseInt(e.target.value) || 0 }
                        })}
                        min="0"
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* QPS Capacity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">QPS Capacity</label>
                      <input
                        type="number"
                        value={formData.cost_profile?.qps_capacity || 0}
                        onChange={(e) => setFormData({
                          ...formData,
                          cost_profile: { ...formData.cost_profile, qps_capacity: parseInt(e.target.value) || 0 }
                        })}
                        min="0"
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Latency */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Latency (ms)</label>
                      <input
                        type="number"
                        value={formData.cost_profile?.latency_ms || 0}
                        onChange={(e) => setFormData({
                          ...formData,
                          cost_profile: { ...formData.cost_profile, latency_ms: parseInt(e.target.value) || 0 }
                        })}
                        min="0"
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Raw JSON Editor */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Cost Profile (JSON)</label>
                      <textarea
                        value={JSON.stringify(formData.cost_profile, null, 2)}
                        onChange={(e) => {
                          try {
                            setFormData({ ...formData, cost_profile: JSON.parse(e.target.value) });
                          } catch {
                            // Allow invalid JSON while typing
                          }
                        }}
                        rows={6}
                        className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                {activeTab === 'synergies' && isEditing && (
                  <>
                    {/* Existing Synergies */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">
                          Component Synergies ({componentSynergies.length})
                        </h4>
                        <button
                          onClick={() => setShowSynergyForm(!showSynergyForm)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Plus className="w-4 h-4" />
                          Add Synergy
                        </button>
                      </div>

                      {loadingSynergies ? (
                        <div className="text-center py-4">
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        </div>
                      ) : componentSynergies.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                          <Link2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No synergies defined for this component</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {componentSynergies.map((synergy) => {
                            const typeConfig = SYNERGY_TYPE_CONFIG[synergy.synergy_type] || SYNERGY_TYPE_CONFIG.neutral;
                            const TypeIcon = typeConfig.icon;
                            return (
                              <div
                                key={synergy.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                                    <TypeIcon className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm">
                                      {synergy.synergy_name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      with <span className="font-medium">{synergy.other_component_name}</span>
                                      {synergy.synergy_type === 'positive' && synergy.bonus_points > 0 && (
                                        <span className="ml-2 text-green-600">+{synergy.bonus_points} pts</span>
                                      )}
                                      {synergy.synergy_type === 'negative' && synergy.penalty_points > 0 && (
                                        <span className="ml-2 text-red-600">-{synergy.penalty_points} pts</span>
                                      )}
                                    </div>
                                    {synergy.description && (
                                      <div className="text-xs text-gray-400 mt-1">{synergy.description}</div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteSynergy(synergy.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* New Synergy Form */}
                    {showSynergyForm && (
                      <div className="border rounded-lg p-4 bg-blue-50 space-y-3">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-700">Create New Synergy</h5>
                          <button
                            onClick={resetSynergyForm}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Target Component */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Partner Component *</label>
                          <select
                            value={synergyForm.component_b_id}
                            onChange={(e) => setSynergyForm({ ...synergyForm, component_b_id: e.target.value })}
                            className="w-full border rounded px-2 py-1.5 text-sm"
                          >
                            <option value="">Select a component...</option>
                            {components
                              .filter((c) => c.id !== selectedComponent?.id)
                              .map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name} ({c.category})
                                </option>
                              ))}
                          </select>
                        </div>

                        {/* Synergy Type */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Type</label>
                          <div className="flex gap-2">
                            {Object.entries(SYNERGY_TYPE_CONFIG).map(([type, config]) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setSynergyForm({ ...synergyForm, synergy_type: type })}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm ${
                                  synergyForm.synergy_type === type
                                    ? config.color
                                    : 'bg-gray-50 text-gray-600 border-gray-200'
                                }`}
                              >
                                <config.icon className="w-3 h-3" />
                                {config.name}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Synergy Name */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Synergy Name *</label>
                          <input
                            type="text"
                            value={synergyForm.synergy_name}
                            onChange={(e) => setSynergyForm({ ...synergyForm, synergy_name: e.target.value })}
                            placeholder="e.g., Cache-Aside Pattern"
                            className="w-full border rounded px-2 py-1.5 text-sm"
                          />
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            value={synergyForm.description}
                            onChange={(e) => setSynergyForm({ ...synergyForm, description: e.target.value })}
                            placeholder="Brief description of the synergy"
                            className="w-full border rounded px-2 py-1.5 text-sm"
                          />
                        </div>

                        {/* Points */}
                        <div className="grid grid-cols-2 gap-3">
                          {synergyForm.synergy_type === 'positive' && (
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Bonus Points</label>
                              <input
                                type="number"
                                value={synergyForm.bonus_points}
                                onChange={(e) => setSynergyForm({ ...synergyForm, bonus_points: parseInt(e.target.value) || 0 })}
                                min="0"
                                className="w-full border rounded px-2 py-1.5 text-sm"
                              />
                            </div>
                          )}
                          {synergyForm.synergy_type === 'negative' && (
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Penalty Points</label>
                              <input
                                type="number"
                                value={synergyForm.penalty_points}
                                onChange={(e) => setSynergyForm({ ...synergyForm, penalty_points: parseInt(e.target.value) || 0 })}
                                min="0"
                                className="w-full border rounded px-2 py-1.5 text-sm"
                              />
                            </div>
                          )}
                        </div>

                        {/* Create Button */}
                        <button
                          onClick={handleCreateSynergy}
                          disabled={processing}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                        >
                          {processing ? 'Creating...' : 'Create Synergy'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Save Button */}
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={handleSave}
                  disabled={processing}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-5 h-5" />
                  {processing ? 'Saving...' : (isCreating ? 'Create Component' : 'Save Changes')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentEditor;
